import React, { useState, useEffect } from 'react';
import { Link2, Unlink, AlertCircle, CheckCircle, MessageSquare, Repeat, RefreshCw, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface InstagramAccount {
  id: string;
  page_name: string;
  instagram_account_id: string;
  has_dm: boolean;
  has_comment_exchange: boolean;
  indexed_posts_count?: number;
}

interface LinkedPair {
  instagram_id: string;
  instagram_name: string;
  instagram_account_id: string;
  instagram_has_dm: boolean;
  instagram_has_comment_exchange: boolean;
  instagram_indexed_posts_count: number;
  direct_id: string;
  direct_name: string;
  direct_account_id: string;
  direct_has_dm: boolean;
  direct_has_comment_exchange: boolean;
  is_bidirectional: boolean;
}

export function InstagramDirectLinking() {
  const [availableBusiness, setAvailableBusiness] = useState<InstagramAccount[]>([]);
  const [availableDirect, setAvailableDirect] = useState<InstagramAccount[]>([]);
  const [linkedPairs, setLinkedPairs] = useState<LinkedPair[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [selectedDirect, setSelectedDirect] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [unlinkModal, setUnlinkModal] = useState<{ visible: boolean; pair: LinkedPair | null }>({
    visible: false,
    pair: null,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && unlinkModal.visible && !processing) {
        closeUnlinkModal();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [unlinkModal.visible, processing]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [businessRes, directRes, linkedRes] = await Promise.all([
        supabase.rpc('get_available_instagram_business'),
        supabase.rpc('get_available_instagram_direct'),
        supabase.rpc('get_linked_instagram_pairs'),
      ]);

      if (businessRes.error) throw businessRes.error;
      if (directRes.error) throw directRes.error;
      if (linkedRes.error) throw linkedRes.error;

      setAvailableBusiness(businessRes.data || []);
      setAvailableDirect(directRes.data || []);
      setLinkedPairs(linkedRes.data || []);
    } catch (error) {
      console.error('Error loading Instagram accounts:', error);
      toast.error('Nie udało się załadować kont');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    if (!selectedBusiness || !selectedDirect) {
      toast.error('Wybierz oba konta');
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.rpc('link_instagram_to_direct', {
        p_instagram_account_id: selectedBusiness,
        p_instagram_direct_account_id: selectedDirect,
      });

      if (error) throw error;

      if (data.success) {
        toast.success('Konta połączone! System będzie używał Instagram Direct dla wiadomości i komentarzy.');
        setSelectedBusiness('');
        setSelectedDirect('');
        await loadData();
      } else {
        toast.error(data.error || 'Nie udało się połączyć kont');
      }
    } catch (error: any) {
      console.error('Error linking accounts:', error);
      toast.error(error.message || 'Nie udało się połączyć kont');
    } finally {
      setProcessing(false);
    }
  };

  const openUnlinkModal = (pair: LinkedPair) => {
    setUnlinkModal({ visible: true, pair });
  };

  const closeUnlinkModal = () => {
    setUnlinkModal({ visible: false, pair: null });
  };

  const confirmUnlink = async () => {
    if (!unlinkModal.pair) return;

    setProcessing(true);
    try {
      const { data, error } = await supabase.rpc('unlink_instagram_from_direct', {
        p_instagram_account_id: unlinkModal.pair.instagram_id,
      });

      if (error) throw error;

      if (data.success) {
        toast.success('Konta rozłączone');
        closeUnlinkModal();
        await loadData();
      } else {
        toast.error(data.error || 'Nie udało się rozłączyć kont');
      }
    } catch (error: any) {
      console.error('Error unlinking accounts:', error);
      toast.error(error.message || 'Nie udało się rozłączyć kont');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-100">
            <p className="font-medium mb-1">Instagram Business + Instagram Direct = Pełna funkcjonalność</p>
            <div className="space-y-2 text-blue-200 mt-2">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Social Inbox:</span> Instagram Direct używa nowego API dla pełnej obsługi wiadomości prywatnych.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Repeat className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Comment Exchange:</span> Instagram Business pobiera posty, Instagram Direct publikuje komentarze.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Linked Pairs Section */}
      {linkedPairs.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-white">Połączone konta</h4>
          {linkedPairs.map((pair) => (
            <div
              key={pair.instagram_id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  {/* Instagram Business */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-white">{pair.instagram_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-blue-900 text-blue-200 rounded-full">
                        Instagram Business
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">ID: {pair.instagram_account_id}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {pair.instagram_has_dm && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-900 text-green-200 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          DM włączone
                        </span>
                      )}
                      {pair.instagram_has_comment_exchange && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-900 text-purple-200 text-xs rounded-full">
                          <Repeat className="w-3 h-3" />
                          Comment Exchange
                        </span>
                      )}
                      {pair.instagram_indexed_posts_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-900 text-blue-200 text-xs rounded-full">
                          <MessageSquare className="w-3 h-3" />
                          {pair.instagram_indexed_posts_count} postów
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Connection Arrow */}
                  <div className="flex items-center gap-2 text-green-400">
                    <div className="h-px flex-1 bg-green-600"></div>
                    <Link2 className="w-4 h-4" />
                    <div className="h-px flex-1 bg-green-600"></div>
                  </div>

                  {/* Instagram Direct */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-white">{pair.direct_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-pink-900 text-pink-200 rounded-full">
                        Instagram Direct
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">ID: {pair.direct_account_id}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {pair.direct_has_dm && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-900 text-green-200 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Social Inbox
                        </span>
                      )}
                      {pair.direct_has_comment_exchange && pair.is_bidirectional && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-900 text-purple-200 text-xs rounded-full">
                          <Repeat className="w-3 h-3" />
                          Comment Exchange
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Unlink Button */}
                <button
                  onClick={() => openUnlinkModal(pair)}
                  disabled={processing}
                  className="ml-4 flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  <Unlink className="w-4 h-4" />
                  Rozłącz
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link New Pair Section */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-white mb-4">Połącz nowe konta</h4>

        {availableBusiness.length === 0 && availableDirect.length === 0 ? (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-100">
                <p className="font-medium mb-1">Brak wolnych kont do połączenia</p>
                <p className="text-yellow-200">
                  Wszystkie dostępne konta Instagram Business i Instagram Direct są już połączone.
                  Podłącz nowe konta aby móc je połączyć.
                </p>
              </div>
            </div>
          </div>
        ) : availableBusiness.length === 0 ? (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-100">
                <p className="font-medium mb-1">Brak wolnych kont Instagram Business</p>
                <p className="text-yellow-200">
                  Wszystkie konta Instagram Business są już połączone. Podłącz nowe konto Instagram Business aby móc je połączyć.
                </p>
              </div>
            </div>
          </div>
        ) : availableDirect.length === 0 ? (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-100">
                <p className="font-medium mb-1">Brak wolnych kont Instagram Direct</p>
                <p className="text-yellow-200">
                  Wszystkie konta Instagram Direct są już połączone. Podłącz nowe konto Instagram Direct aby móc je połączyć.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Instagram Business Select */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Instagram Business
                </label>
                <select
                  value={selectedBusiness}
                  onChange={(e) => setSelectedBusiness(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Wybierz konto --</option>
                  {availableBusiness.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.page_name} ({account.instagram_account_id})
                    </option>
                  ))}
                </select>
                {selectedBusiness && availableBusiness.find(a => a.id === selectedBusiness) && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {availableBusiness.find(a => a.id === selectedBusiness)?.has_comment_exchange && (
                      <span className="text-xs px-2 py-1 bg-purple-900 text-purple-200 rounded">
                        Comment Exchange
                      </span>
                    )}
                    {availableBusiness.find(a => a.id === selectedBusiness)?.indexed_posts_count! > 0 && (
                      <span className="text-xs px-2 py-1 bg-blue-900 text-blue-200 rounded">
                        {availableBusiness.find(a => a.id === selectedBusiness)?.indexed_posts_count} postów
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Instagram Direct Select */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Instagram Direct
                </label>
                <select
                  value={selectedDirect}
                  onChange={(e) => setSelectedDirect(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="">-- Wybierz konto --</option>
                  {availableDirect.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.page_name} ({account.instagram_account_id})
                    </option>
                  ))}
                </select>
                {selectedDirect && availableDirect.find(a => a.id === selectedDirect) && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {availableDirect.find(a => a.id === selectedDirect)?.has_dm && (
                      <span className="text-xs px-2 py-1 bg-green-900 text-green-200 rounded">
                        Social Inbox
                      </span>
                    )}
                    {availableDirect.find(a => a.id === selectedDirect)?.has_comment_exchange && (
                      <span className="text-xs px-2 py-1 bg-purple-900 text-purple-200 rounded">
                        Comment Exchange
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleLink}
              disabled={!selectedBusiness || !selectedDirect || processing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {processing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Łączenie...
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5" />
                  Połącz konta
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Unlink Confirmation Modal */}
      {unlinkModal.visible && unlinkModal.pair && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeUnlinkModal}
        >
          <div
            className="bg-gray-800 rounded-lg max-w-md w-full border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Potwierdź rozłączenie</h3>
              <button
                onClick={closeUnlinkModal}
                disabled={processing}
                className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <div className="bg-red-900 border border-red-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-100">
                    <p className="font-medium mb-1">Czy na pewno chcesz rozłączyć te konta?</p>
                    <p className="text-red-200">
                      Po rozłączeniu system przestanie używać Instagram Direct dla wiadomości i komentarzy tych kont.
                    </p>
                  </div>
                </div>
              </div>

              {/* Accounts Info */}
              <div className="space-y-3">
                <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-white">{unlinkModal.pair.instagram_name}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-900 text-blue-200 rounded-full">
                      Instagram Business
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">ID: {unlinkModal.pair.instagram_account_id}</p>
                </div>

                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-2 text-red-400">
                    <div className="h-px w-12 bg-red-600"></div>
                    <Unlink className="w-4 h-4" />
                    <div className="h-px w-12 bg-red-600"></div>
                  </div>
                </div>

                <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-white">{unlinkModal.pair.direct_name}</span>
                    <span className="text-xs px-2 py-0.5 bg-pink-900 text-pink-200 rounded-full">
                      Instagram Direct
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">ID: {unlinkModal.pair.direct_account_id}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={closeUnlinkModal}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={confirmUnlink}
                disabled={processing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Rozłączanie...
                  </>
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    Rozłącz konta
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
